import { Fragment, type ReactNode, useEffect, useState } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignCommercialOutcome,
  CampaignSendOutcome,
  CommercialOutcomeWindowDays,
  LeadSourceCommercialOutcome,
  MarketingCategory,
  MarketingResults,
} from "@shared-core/data/marketing-types";
import {
  sendLabel,
  type SendLabelInput,
} from "@shared-core/domain/campaign-sends";
import { formatUplift } from "@shared-core/domain/campaign-uplift";
import {
  describeProductScope,
  type ProductScope,
} from "@shared-core/domain/commercial-outcome-scope";
import { formatOrderedShare } from "@shared-core/domain/campaign-rep-outreach";
import {
  CAMPAIGN_GOAL_LABELS,
  outcomeMixLine,
  outcomeMixTotal,
} from "@shared-core/domain/direct-mail-campaigns";
import { plural } from "@shared-core/domain/deadline";
import {
  formatCount,
  formatCurrencyGbp,
  formatDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { useCanManageMarketing, useCanReadMarketing } from "../app/auth/hooks";
import { CampaignPerformanceSection } from "../app/components/domain/marketing/CampaignPerformanceSection";
import { CommercialOutcomeEnquiriesDrawer } from "../app/components/domain/marketing/CommercialOutcomeEnquiriesDrawer";
import {
  CommercialOutcomeOrdersDrawer,
  type CommercialOutcomeOrdersSource,
} from "../app/components/domain/marketing/CommercialOutcomeOrdersDrawer";
import { ChannelIcon } from "../app/components/domain/marketing/CampaignStepDisplay";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import {
  CHANNEL_ICONS,
  CHANNEL_LABELS,
  campaignDisplayStatus,
  campaignStatusLabel,
  campaignStatusVariant,
} from "../app/components/domain/marketing/marketingDisplay";
import { Badge } from "../app/components/primitives/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../app/components/primitives/tabs";
import { Button } from "../app/components/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

// The window the Commercial outcomes tab opens on. The API keeps the route's
// default warm in its cache, so this must match it — checked against the
// committed OpenAPI schema in marketingResultsWindowDefaults.test.ts.
export const DEFAULT_COMMERCIAL_OUTCOMES_WINDOW: CommercialOutcomeWindowDays = 30;

const WINDOW_OPTIONS: CommercialOutcomeWindowDays[] = [
  5, 7, 10, 14, 30, 45, 90,
];

type OrdersFilter = "all" | "new_category" | "reactivated";

/** One send of a project, narrowing a drill-in drawer to what it was credited. */
interface DrawerSend {
  stepId: string;
  label: string;
}

function toSendLabelInput(send: CampaignSendOutcome): SendLabelInput {
  return {
    id: send.step_id,
    order: send.position,
    follows_step_id: send.follows_step_id,
  };
}

/** "Tranche N" / "Tranche N Reminder" for an email send when the project
 * has 2+ of them (the campaign workspace's own labels); "Follow-up to
 * Mailing 1" for an email following up a mailing or call round, however many
 * emails there are; the channel name for a lone email send and for call /
 * enclosure / direct-mail steps, which are never called a Send but still
 * take credit under the same rule. */
function sendDisplayLabel(
  send: CampaignSendOutcome,
  sends: CampaignSendOutcome[],
): string {
  if (send.channel !== "email") return CHANNEL_LABELS[send.channel];
  const emailSends = sends
    .filter((s) => s.channel === "email")
    .map(toSendLabelInput);
  const followUpSources = sends
    .filter((s) => s.channel !== "email")
    .map((s) => ({ id: s.step_id, name: s.step_name }));
  const followsNonEmail = followUpSources.some(
    (s) => s.id === send.follows_step_id,
  );
  if (emailSends.length < 2 && !followsNonEmail) return CHANNEL_LABELS.email;
  return sendLabel(toSendLabelInput(send), emailSends, followUpSources);
}

/** A mailing's or call round's own figures on its per-send row: its name,
 * when it went and to which tiers, and what it did (direct-mail campaigns). */
function DirectMailSendCell({ send }: { send: CampaignSendOutcome }) {
  const audience =
    send.target_tier_names.length > 0
      ? send.target_tier_names.join(" + ")
      : "Follow-up";
  const recorded = outcomeMixTotal(send.call_round?.outcome_mix);
  const outcomeLine = outcomeMixLine(send.call_round?.outcome_mix);
  return (
    <>
      <div className="flex items-center gap-1.5 text-sm text-foreground">
        <ChannelIcon channel={send.channel} className="size-3.5" />
        {send.step_name ?? CHANNEL_LABELS[send.channel]}
      </div>
      <div className="text-xs text-muted-foreground">
        {send.mailing ? "Posted" : "Due"} {formatDateOrDash(send.sent_at)} ·{" "}
        {audience}
      </div>
      <div className="text-xs text-muted-foreground">
        {send.mailing
          ? [
              `${formatCount(send.mailing.posted)} posted`,
              send.mailing.delivered
                ? `${formatCount(send.mailing.delivered)} delivered`
                : null,
              send.mailing.returned
                ? `${formatCount(send.mailing.returned)} returned`
                : null,
              `${formatCount(send.mailing.not_included)} not included`,
            ]
              .filter(Boolean)
              .join(" · ")
          : send.call_round
            ? `${formatCount(send.call_round.call_tickets)} call ${plural("ticket", send.call_round.call_tickets)} · ${formatCount(recorded)} ${plural("outcome", recorded)} recorded`
            : null}
      </div>
      {outcomeLine && (
        <div className="whitespace-normal text-xs text-muted-foreground">
          {outcomeLine}
        </div>
      )}
    </>
  );
}

/** A send's name on the Results breakdown: a mailing's or call round's own
 * name, otherwise the email label ("Tranche N"). */
function sendRowLabel(
  send: CampaignSendOutcome,
  sends: CampaignSendOutcome[],
): string {
  return send.step_name ?? sendDisplayLabel(send, sends);
}

/** Under a posted mailing's row: what its customers did once the next send
 * to them went out (Lauren, 15 Oct 2026). A cut of that send's own figures,
 * so it says so and is never added to anything; or "Follow-up not sent yet"
 * while no later send has gone to its customers. */
function MailingFollowUpRows({
  mailingSend,
  sends,
}: {
  mailingSend: CampaignSendOutcome;
  sends: CampaignSendOutcome[];
}) {
  const mailing = mailingSend.mailing;
  if (!mailing || mailing.posted === 0) return null;
  const mailingLabel = sendRowLabel(mailingSend, sends);
  if (mailing.follow_ups.length === 0) {
    return (
      <TableRow className="bg-muted/40">
        <TableCell className="pl-14">
          <div className="text-sm text-foreground">
            {mailingLabel} + follow-up
          </div>
        </TableCell>
        <TableCell colSpan={9} className="text-sm text-muted-foreground">
          Follow-up not sent yet
        </TableCell>
      </TableRow>
    );
  }
  return (
    <>
      {mailing.follow_ups.map((cut) => {
        const followUp = sends.find((s) => s.step_id === cut.follow_up_step_id);
        const followUpLabel = followUp
          ? sendRowLabel(followUp, sends)
          : "next send";
        return (
          <TableRow
            key={`${mailingSend.step_id}+${cut.follow_up_step_id}`}
            className="bg-muted/40"
          >
            <TableCell className="pl-14">
              <div className="whitespace-normal text-sm text-foreground">
                {mailingLabel} + follow-up ({followUpLabel})
              </div>
              <div className="text-xs text-muted-foreground">
                {formatCount(cut.customer_count)}{" "}
                {plural("customer", cut.customer_count)} got both
              </div>
              <div className="whitespace-normal text-xs text-muted-foreground">
                Part of the figures for {followUpLabel}: customers who also got
                the mailing. Not added to the totals.
              </div>
            </TableCell>
            <TableCell>{formatCount(cut.enquired_customer_count)}</TableCell>
            <TableCell>{formatCount(cut.order_count)}</TableCell>
            <TableCell>{formatCurrencyGbp(cut.sales_amount)}</TableCell>
            <TableCell className="text-muted-foreground">—</TableCell>
            <TableCell>{formatCount(cut.new_category_customers)}</TableCell>
            <TableCell>{formatCount(cut.reactivated_customers)}</TableCell>
            {/* Clickers and rep outreach aren't cut by mailing recipients. */}
            <TableCell className="text-muted-foreground">—</TableCell>
            <TableCell className="text-muted-foreground">—</TableCell>
            <TableCell className="text-muted-foreground">—</TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

/** A count that opens a drill-in when non-zero, plain text when zero. */
function DrillInCount({
  count,
  onOpen,
}: {
  count: number;
  onOpen: () => void;
}) {
  if (count === 0) return <>{count}</>;
  return (
    <button
      type="button"
      className="underline hover:text-primary"
      onClick={onOpen}
    >
      {count}
    </button>
  );
}

/** What a row's order and enquiry figures count — every product, or only
 * the campaign's / landing page's product scope — shown on every row so an
 * unscoped total is never mistaken for a product's. */
function ProductScopeLine({ scope }: { scope: ProductScope }) {
  return (
    <div className="text-xs font-normal text-muted-foreground">
      Counts: {describeProductScope(scope)}
    </div>
  );
}

/** The matched-cohort caption under a Commercial outcomes row's name. It sits
 * in the Campaign cell rather than a column of its own so the table fits a
 * laptop screen; it wraps rather than widening the column. */
function MatchedCohortLine({ children }: { children: ReactNode }) {
  return (
    <div className="mt-0.5 whitespace-normal text-xs font-normal text-muted-foreground">
      {children}
    </div>
  );
}

function LeadSourceOutcomeRow({
  outcome,
  onOpenOrders,
}: {
  outcome: LeadSourceCommercialOutcome;
  onOpenOrders: (filter: OrdersFilter, title: string) => void;
}) {
  return (
    <TableRow>
      <TableCell className="font-medium text-foreground">
        <div className="flex items-center gap-1.5">
          <span>{outcome.lead_source_name}</span>
          <Badge variant="secondary" className="cursor-default">
            Landing page
          </Badge>
        </div>
        <ProductScopeLine
          scope={{
            productKeywords: outcome.product_keywords,
            categories: outcome.marketing_categories,
          }}
        />
        <MatchedCohortLine>
          {formatCount(outcome.resolved_submission_count)} of{" "}
          {formatCount(outcome.submission_count)} submissions matched
        </MatchedCohortLine>
      </TableCell>
      <TableCell>
        {/* A form submission is the enquiry, so Enquired is the number of
            submissions, opening the landing page's own leads list. */}
        {outcome.submission_count === 0 ? (
          0
        ) : (
          <Link
            to={paths.marketingLandingPageSubmissions({
              leadSourceId: outcome.lead_source_id,
            })}
            className="underline hover:text-primary"
          >
            {outcome.submission_count}
          </Link>
        )}
      </TableCell>
      <TableCell>
        <DrillInCount
          count={outcome.order_count}
          onOpen={() => onOpenOrders("all", "Orders placed")}
        />
      </TableCell>
      <TableCell>{formatCurrencyGbp(outcome.sales_amount)}</TableCell>
      {/* No normal ordering / uplift: most leads are new or prospective
          customers with a normal of about £0, so uplift would repeat Sales. */}
      <TableCell className="text-muted-foreground">—</TableCell>
      <TableCell>
        <DrillInCount
          count={outcome.new_category_customers}
          onOpen={() => onOpenOrders("new_category", "New products")}
        />
      </TableCell>
      <TableCell>
        <DrillInCount
          count={outcome.reactivated_customers}
          onOpen={() => onOpenOrders("reactivated", "Reactivated customers")}
        />
      </TableCell>
      {/* No email was sent, so there are no clicks to count. */}
      <TableCell className="text-muted-foreground">—</TableCell>
      {/* Landing-page leads aren't campaign tickets. */}
      <TableCell className="text-muted-foreground">—</TableCell>
      <TableCell className="text-muted-foreground">—</TableCell>
    </TableRow>
  );
}

/** The two rep-outreach cells of a campaign row: were its tickets worked,
 * and how did the customers a rep reached do against the rest? */
function RepOutreachCells({ outcome }: { outcome: CampaignCommercialOutcome }) {
  const outreach = outcome.rep_outreach;
  if (outreach === null) {
    // The ticket figures are built hourly; null only before the first build.
    return (
      <>
        <TableCell className="text-muted-foreground">—</TableCell>
        <TableCell className="text-muted-foreground">—</TableCell>
      </>
    );
  }
  if (outreach.tickets_raised === 0) {
    return (
      <>
        <TableCell className="text-muted-foreground">No tickets</TableCell>
        <TableCell className="text-muted-foreground">—</TableCell>
      </>
    );
  }
  const withoutOutreach = outcome.order_count - outreach.outreach_order_count;
  const actionedPct = Math.round(
    (outreach.tickets_actioned / outreach.tickets_raised) * 100,
  );
  return (
    <>
      <TableCell>
        <div className="whitespace-nowrap">
          {formatCount(outreach.tickets_actioned)} of{" "}
          {formatCount(outreach.tickets_raised)}
        </div>
        <div className="text-xs text-muted-foreground">{actionedPct}%</div>
      </TableCell>
      <TableCell>
        <div className="whitespace-nowrap">
          {formatCount(outreach.outreach_order_count)}{" "}
          {plural("order", outreach.outreach_order_count)} ·{" "}
          {formatCurrencyGbp(outreach.outreach_sales_amount)}
        </div>
        <div className="text-xs text-muted-foreground whitespace-nowrap">
          {formatCount(withoutOutreach)} {plural("order", withoutOutreach)}{" "}
          without
        </div>
        <div className="text-xs text-muted-foreground whitespace-nowrap">
          Ordered:{" "}
          {formatOrderedShare(
            outreach.reached_ordered_customer_count,
            outreach.reached_customer_count,
          )}{" "}
          reached ·{" "}
          {formatOrderedShare(
            outreach.not_reached_ordered_customer_count,
            outreach.not_reached_customer_count,
          )}{" "}
          not
        </div>
      </TableCell>
    </>
  );
}

function CampaignOutcomeRows({
  outcome,
  isExpanded,
  onToggleExpanded,
  onOpenOrders,
  onOpenEnquiries,
}: {
  outcome: CampaignCommercialOutcome;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onOpenOrders: (
    filter: OrdersFilter,
    title: string,
    send: DrawerSend | null,
  ) => void;
  onOpenEnquiries: (send: DrawerSend | null) => void;
}) {
  // A single-send project's one breakdown row would just repeat its totals;
  // a tiered campaign always has its per-tier funnel to show.
  const hasBreakdown = outcome.sends.length > 1 || outcome.tiers.length > 0;
  const hasDirectMailRows = outcome.sends.some(
    (s) => s.mailing !== null || s.call_round !== null,
  );
  return (
    <>
      <TableRow>
        <TableCell className="font-medium text-foreground">
          <div className="flex items-center gap-1.5">
            {hasBreakdown && (
              <button
                type="button"
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? "Hide" : "Show"} results by send for ${outcome.campaign_name}`}
                className="-ml-1 rounded p-0.5 text-muted-foreground hover:text-foreground"
                onClick={onToggleExpanded}
              >
                <ChevronRight
                  className={`size-4 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                />
              </button>
            )}
            <span>{outcome.campaign_name}</span>
            {outcome.campaign_kind === "always_on" && (
              <Badge variant="outline" className="font-normal">
                Always on
              </Badge>
            )}
          </div>
          {outcome.goal && (
            <div className="text-xs font-normal text-muted-foreground">
              Goal: {CAMPAIGN_GOAL_LABELS[outcome.goal]}
            </div>
          )}
          <ProductScopeLine
            scope={{
              productKeywords: outcome.product_keywords,
              categories: outcome.marketing_categories,
            }}
          />
          <MatchedCohortLine>
            {formatCount(outcome.resolved_cohort_size)} of{" "}
            {formatCount(outcome.cohort_size)} contacts matched (
            {formatCount(outcome.verified_cohort_size)} Tharstern-verified)
          </MatchedCohortLine>
        </TableCell>
        <TableCell>
          <DrillInCount
            count={outcome.enquired_customer_count}
            onOpen={() => onOpenEnquiries(null)}
          />
        </TableCell>
        <TableCell>
          <DrillInCount
            count={outcome.order_count}
            onOpen={() => onOpenOrders("all", "Orders placed", null)}
          />
        </TableCell>
        <TableCell>
          <div>{formatCurrencyGbp(outcome.sales_amount)}</div>
          {outcome.uplift_is_comparable &&
            outcome.normal_ordering_amount !== null && (
              <div className="text-xs text-muted-foreground">
                normal {formatCurrencyGbp(outcome.normal_ordering_amount)}
              </div>
            )}
        </TableCell>
        <TableCell>
          {outcome.uplift_is_comparable ? (
            <>
              <div className="whitespace-nowrap">
                {formatUplift(outcome.uplift_amount, outcome.uplift_pct)}
              </div>
              {outcome.short_history_customer_count > 0 && (
                <div className="text-xs text-muted-foreground">
                  {formatCount(outcome.short_history_customer_count)} with under
                  a year's history
                </div>
              )}
              {outcome.normal_ordering_amount !== null && (
                <div className="text-xs text-muted-foreground">
                  vs {formatCount(outcome.comparison_customer_count)} customers
                  not emailed
                </div>
              )}
            </>
          ) : (
            <>
              <div>Not a fair comparison</div>
              <div className="text-xs text-muted-foreground">
                {Math.round(
                  (outcome.prior_year_buyer_count /
                    outcome.sent_customer_count) *
                    100,
                )}
                % of recipients were already buying
              </div>
            </>
          )}
        </TableCell>
        <TableCell>
          <DrillInCount
            count={outcome.new_category_customers}
            onOpen={() => onOpenOrders("new_category", "New products", null)}
          />
        </TableCell>
        <TableCell>
          <DrillInCount
            count={outcome.reactivated_customers}
            onOpen={() =>
              onOpenOrders("reactivated", "Reactivated customers", null)
            }
          />
        </TableCell>
        <TableCell>{outcome.unique_clicker_count}</TableCell>
        <RepOutreachCells outcome={outcome} />
      </TableRow>

      {hasBreakdown &&
        isExpanded &&
        outcome.sends.map((send) => {
          const drawerSend: DrawerSend = {
            stepId: send.step_id,
            label: sendRowLabel(send, outcome.sends),
          };
          return (
            <Fragment key={send.step_id}>
              <TableRow className="bg-muted/40">
                <TableCell className="pl-9">
                  {send.mailing || send.call_round ? (
                    <DirectMailSendCell send={send} />
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 text-sm text-foreground">
                        {/* Beside mailings and call rounds, emails show their
                          channel too. */}
                        {hasDirectMailRows && (
                          <ChannelIcon
                            channel={send.channel}
                            className="size-3.5"
                          />
                        )}
                        {drawerSend.label}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Sent {formatDateOrDash(send.sent_at)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatCount(send.contacts_sent)} contacts sent
                      </div>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  <DrillInCount
                    count={send.enquired_customer_count}
                    onOpen={() => onOpenEnquiries(drawerSend)}
                  />
                </TableCell>
                <TableCell>
                  <DrillInCount
                    count={send.order_count}
                    onOpen={() =>
                      onOpenOrders("all", "Orders placed", drawerSend)
                    }
                  />
                </TableCell>
                <TableCell>
                  <div>{formatCurrencyGbp(send.sales_amount)}</div>
                  {send.uplift_is_comparable &&
                    send.normal_ordering_amount !== null && (
                      <div className="text-xs text-muted-foreground">
                        normal {formatCurrencyGbp(send.normal_ordering_amount)}
                      </div>
                    )}
                </TableCell>
                {/* Per-send uplift: the campaign's normal ordering split over
                  the days each send's sales are counted for, so the sends add
                  up to the campaign (campaign-performance-targets/02 §7). */}
                <TableCell>
                  {send.uplift_is_comparable ? (
                    <div className="whitespace-nowrap">
                      {formatUplift(send.uplift_amount, send.uplift_pct)}
                    </div>
                  ) : (
                    <div className="text-muted-foreground">
                      Not a fair comparison
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <DrillInCount
                    count={send.new_category_customers}
                    onOpen={() =>
                      onOpenOrders("new_category", "New products", drawerSend)
                    }
                  />
                </TableCell>
                <TableCell>
                  <DrillInCount
                    count={send.reactivated_customers}
                    onOpen={() =>
                      onOpenOrders(
                        "reactivated",
                        "Reactivated customers",
                        drawerSend,
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  {send.channel === "email" ? (
                    <>
                      {send.unique_clicker_count}
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({send.new_clicker_count} new ·{" "}
                        {send.repeat_clicker_count} repeat)
                      </span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                {/* Rep outreach is campaign-level only. */}
                <TableCell className="text-muted-foreground">—</TableCell>
                <TableCell className="text-muted-foreground">—</TableCell>
              </TableRow>
              <MailingFollowUpRows mailingSend={send} sends={outcome.sends} />
            </Fragment>
          );
        })}
      {hasBreakdown &&
        isExpanded &&
        outcome.tiers.map((tier) => (
          <TableRow key={tier.tier_id} className="bg-muted/40">
            <TableCell className="pl-9">
              <div className="text-sm text-foreground">
                Tier: {tier.tier_name}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatCount(tier.people)}{" "}
                {tier.people === 1 ? "person" : "people"}
              </div>
            </TableCell>
            {/* A tier's funnel, in people: orders are credited per customer
                account, which can span tiers, so they aren't split by tier. */}
            <TableCell colSpan={9} className="text-sm text-muted-foreground">
              <div>
                {[
                  `${formatCount(tier.posted)} posted`,
                  tier.delivered
                    ? `${formatCount(tier.delivered)} delivered`
                    : null,
                  tier.returned
                    ? `${formatCount(tier.returned)} returned`
                    : null,
                  `${formatCount(tier.emailed)} emailed`,
                  `${formatCount(tier.called)} called`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              {outcomeMixLine(tier.outcome_mix) && (
                <div className="whitespace-normal text-xs">
                  {outcomeMixLine(tier.outcome_mix)}
                </div>
              )}
            </TableCell>
          </TableRow>
        ))}
    </>
  );
}

export function MarketingResultsPage() {
  const dataAdapter = useDataAdapter();
  const canReadMarketing = useCanReadMarketing();
  const canManageMarketing = useCanManageMarketing();
  const [results, setResults] = useState<MarketingResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [windowDays, setWindowDays] = useState<CommercialOutcomeWindowDays>(
    DEFAULT_COMMERCIAL_OUTCOMES_WINDOW,
  );
  const [campaignOutcomes, setCampaignOutcomes] = useState<
    CampaignCommercialOutcome[] | null
  >(null);
  const [leadSourceOutcomes, setLeadSourceOutcomes] = useState<
    LeadSourceCommercialOutcome[] | null
  >(null);
  const [outcomesLoading, setOutcomesLoading] = useState(true);
  const [outcomesError, setOutcomesError] = useState(false);
  const [leadSourceOutcomesLoading, setLeadSourceOutcomesLoading] =
    useState(true);
  const [leadSourceOutcomesError, setLeadSourceOutcomesError] = useState(false);

  // Lead-source outcomes don't depend on windowDays (open-ended attribution
  // has no window), so they're fetched once and rendered below whichever
  // campaign window is currently selected.
  const anyOutcomesLoading = outcomesLoading || leadSourceOutcomesLoading;
  const anyOutcomesError = outcomesError || leadSourceOutcomesError;
  const outcomeRowCount =
    (campaignOutcomes?.length ?? 0) + (leadSourceOutcomes?.length ?? 0);

  const [ordersDrawer, setOrdersDrawer] = useState<{
    source: CommercialOutcomeOrdersSource;
    name: string;
    filter: OrdersFilter;
    title: string;
    productKeywords: string[] | null;
    categories: MarketingCategory[] | null;
  } | null>(null);
  // Separate from the drawer's content, which is kept after closing so the
  // drawer doesn't show placeholder text while it animates shut.
  const [isOrdersDrawerOpen, setIsOrdersDrawerOpen] = useState(false);
  function openOrdersDrawer(drawer: NonNullable<typeof ordersDrawer>) {
    setOrdersDrawer(drawer);
    setIsOrdersDrawerOpen(true);
  }

  const [enquiriesDrawer, setEnquiriesDrawer] = useState<{
    campaignId: string;
    campaignName: string;
    productKeywords: string[] | null;
    categories: MarketingCategory[] | null;
    send: DrawerSend | null;
  } | null>(null);

  // Campaign rows whose per-send breakdown is open.
  const [expandedCampaignIds, setExpandedCampaignIds] = useState<
    ReadonlySet<string>
  >(new Set());

  function toggleExpanded(campaignId: string) {
    setExpandedCampaignIds((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(false);
      try {
        const res = await dataAdapter.getMarketingResults();
        if (!cancelled) setResults(res);
      } catch (e) {
        console.error("marketing.results.load.failed", e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setOutcomesLoading(true);
      setOutcomesError(false);
      try {
        const res = await dataAdapter.getCampaignCommercialOutcomes(windowDays);
        if (!cancelled) setCampaignOutcomes(res);
      } catch (e) {
        console.error("marketing.results.commercial_outcomes.load.failed", e);
        if (!cancelled) setOutcomesError(true);
      } finally {
        if (!cancelled) setOutcomesLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, windowDays]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLeadSourceOutcomesLoading(true);
      setLeadSourceOutcomesError(false);
      try {
        const res = await dataAdapter.getLeadSourceCommercialOutcomes();
        if (!cancelled) setLeadSourceOutcomes(res);
      } catch (e) {
        console.error(
          "marketing.results.lead_source_commercial_outcomes.load.failed",
          e,
        );
        if (!cancelled) setLeadSourceOutcomesError(true);
      } finally {
        if (!cancelled) setLeadSourceOutcomesLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  return (
    <div className="space-y-6">
      <MarketingNav active="results" />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl mb-1">Results</h1>
          <p className="text-sm text-muted-foreground">
            Touch overview across campaigns and channels. Read-only portfolio
            view.
          </p>
        </div>
        {canReadMarketing ? (
          <Button variant="outline" size="sm" asChild>
            <Link to={`${paths.insights()}?tab=campaignimpact`}>
              Campaign impact on sales
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        ) : null}
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground">Loading results…</div>
      )}

      {error && !loading && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
          Couldn't load results. Please try again.
        </div>
      )}

      {!loading && !error && results && (
        <>
          <div className="rounded-lg border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  {results.channels.map((ch) => {
                    const Icon = CHANNEL_ICONS[ch];
                    return (
                      <TableHead key={ch}>
                        <span className="flex items-center gap-1.5">
                          <Icon className="size-4" />
                          {CHANNEL_LABELS[ch]}
                        </span>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.rows.map((row) => (
                  <TableRow key={row.campaign_id}>
                    <TableCell>
                      <Link
                        to={paths.marketingCampaignWorkspace({
                          campaignId: row.campaign_id,
                        })}
                        className="font-medium text-foreground hover:text-primary hover:underline"
                      >
                        {row.campaign_name}
                      </Link>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge
                          variant={campaignStatusVariant(
                            campaignDisplayStatus(row),
                          )}
                        >
                          {campaignStatusLabel(campaignDisplayStatus(row))}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {row.audience_name}
                        </span>
                      </div>
                    </TableCell>
                    {results.channels.map((ch) => {
                      const cell = row.cells.find((c) => c.channel === ch);
                      const label = cell?.label ?? "—";
                      const active = cell?.active ?? false;
                      return (
                        <TableCell
                          key={ch}
                          className={
                            active ? "text-foreground" : "text-muted-foreground"
                          }
                        >
                          {label}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Legend:</span>
            <span>"134 sent" — emails delivered via Mailchimp</span>
            <span>
              "12 posted" — people a mailing reached, once marked posted
            </span>
            <span>
              "8 delivered" / "1 returned" — of those, DPD or DX packs the
              courier delivered, and packs that came back
            </span>
            <span>"60 called" — people a call round raised a ticket for</span>
            <span>"CSV ✓" — enclosure export produced</span>
            <span>"Pending" — not yet activated</span>
            <span>"—" — channel not used</span>
          </div>

          <Tabs defaultValue="outcomes" className="space-y-4">
            <TabsList>
              <TabsTrigger value="outcomes">Commercial outcomes</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
            </TabsList>
            <TabsContent value="outcomes">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-lg">Commercial outcomes</h2>
                    <p className="text-sm text-muted-foreground">
                      Results from each campaign's cohort, counted from when it
                      was sent (so far, for a campaign still running).
                      Correlation, not proof the campaign caused it.
                    </p>
                  </div>
                  <Select
                    value={String(windowDays)}
                    onValueChange={(v) =>
                      setWindowDays(Number(v) as CommercialOutcomeWindowDays)
                    }
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WINDOW_OPTIONS.map((days) => (
                        <SelectItem key={days} value={String(days)}>
                          {days} days
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {anyOutcomesLoading && (
                  <div className="text-sm text-muted-foreground">
                    Loading commercial outcomes…
                  </div>
                )}

                {anyOutcomesError && !anyOutcomesLoading && (
                  <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
                    Couldn't load commercial outcomes. Please try again.
                  </div>
                )}

                {!anyOutcomesLoading &&
                  !anyOutcomesError &&
                  campaignOutcomes &&
                  (outcomeRowCount === 0 ? (
                    <div className="text-sm p-8 text-center border border-border rounded-lg text-muted-foreground">
                      No campaign sends or landing-page submissions yet —
                      commercial outcomes appear once a campaign has sent, or a
                      landing page has active submissions.
                    </div>
                  ) : (
                    <>
                      <div className="rounded-lg border border-border overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {/* The sr-only suffixes name the secondary lines
                              folded into these cells (matched cohort under
                              the name, normal ordering under Sales). */}
                              <TableHead>
                                Campaign
                                <span className="sr-only">
                                  {" "}
                                  and matched cohort
                                </span>
                              </TableHead>
                              <TableHead>Enquired</TableHead>
                              <TableHead>Orders placed</TableHead>
                              <TableHead>
                                Sales
                                <span className="sr-only">
                                  {" "}
                                  and normal ordering
                                </span>
                              </TableHead>
                              <TableHead>Uplift</TableHead>
                              <TableHead>New products</TableHead>
                              <TableHead>Reactivated</TableHead>
                              <TableHead>Unique clickers</TableHead>
                              <TableHead>Tickets actioned</TableHead>
                              <TableHead>
                                After rep outreach
                                <span className="sr-only">
                                  {" "}
                                  and orders without it
                                </span>
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {campaignOutcomes.map((outcome) => (
                              <CampaignOutcomeRows
                                key={outcome.campaign_id}
                                outcome={outcome}
                                isExpanded={expandedCampaignIds.has(
                                  outcome.campaign_id,
                                )}
                                onToggleExpanded={() =>
                                  toggleExpanded(outcome.campaign_id)
                                }
                                onOpenOrders={(filter, title, send) =>
                                  openOrdersDrawer({
                                    source: {
                                      kind: "campaign",
                                      campaignId: outcome.campaign_id,
                                      windowDays,
                                      send,
                                    },
                                    name: outcome.campaign_name,
                                    filter,
                                    title,
                                    productKeywords: outcome.product_keywords,
                                    categories: outcome.marketing_categories,
                                  })
                                }
                                onOpenEnquiries={(send) =>
                                  setEnquiriesDrawer({
                                    campaignId: outcome.campaign_id,
                                    campaignName: outcome.campaign_name,
                                    productKeywords: outcome.product_keywords,
                                    categories: outcome.marketing_categories,
                                    send,
                                  })
                                }
                              />
                            ))}
                            {leadSourceOutcomes?.map((outcome) => (
                              <LeadSourceOutcomeRow
                                key={outcome.lead_source_id}
                                outcome={outcome}
                                onOpenOrders={(filter, title) =>
                                  openOrdersDrawer({
                                    source: {
                                      kind: "lead_source",
                                      leadSourceId: outcome.lead_source_id,
                                    },
                                    name: outcome.lead_source_name,
                                    filter,
                                    title,
                                    productKeywords: outcome.product_keywords,
                                    categories: outcome.marketing_categories,
                                  })
                                }
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Each order and enquiry is credited to the most recent
                        send the customer received before it, so a campaign's
                        sends add up to its total — an order placed before a
                        customer's own first send isn't counted at all. That's
                        timing, not proof a send caused the order. Unique
                        clickers counts each contact once across all of a
                        campaign's sends: "new" had not clicked an earlier send,
                        "repeat" had. A customer who enquires after two
                        different sends counts on each send but once in the
                        campaign's Enquired total, and the same goes for New
                        products. Reactivated counts customers who hadn't
                        ordered in the year before the send that preceded their
                        order, so a campaign's sends add up to its total there
                        too. Landing pages count every order from when each
                        customer submitted the form, with no end date: Enquired
                        is the number of form submissions, and Reactivated
                        counts customers who hadn't ordered in the year before
                        they submitted it.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        A posted mailing's own row is what it did on its own:
                        orders from the day it was posted until each customer's
                        next send. The "+ follow-up" row under it is the
                        customers who got the mailing and then that next send,
                        and what they ordered from the follow-up until their
                        send after it (or the end of the window). It is part of
                        the follow-up's own figures, not extra, so it is never
                        added to a campaign's total.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Normal ordering (the "normal" figure under Sales) is
                        what a campaign's recipients would usually have ordered
                        over the same days without it: each customer's own last
                        12 months, scaled up or down by how customers who
                        weren't emailed did over those same days compared with
                        their own last 12 months. Uplift is Sales minus Normal
                        ordering — again timing, not proof the campaign caused
                        it. A send's normal ordering covers the days its own
                        sales are counted for (from the send to that customer's
                        next one), so a campaign's sends add up to its total.
                        The comparison is weakest when almost everyone was
                        emailed, so each campaign says how many customers it was
                        compared against. Customers with less than a year's
                        ordering history have a low or zero normal figure, so
                        their orders mostly count as uplift; each campaign shows
                        how many of its recipients that applies to. When at
                        least 80% of a campaign's recipients had ordered in the
                        year before it was sent, the customers who weren't
                        emailed are a different kind of customer (mostly
                        occasional or dormant buyers), so Uplift says "Not a
                        fair comparison" instead of showing a number. For those
                        campaigns, New products, Reactivated and the
                        rep-outreach columns are the more useful measures.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Tickets actioned counts a campaign's engagement and call
                        tickets that a person did something on: a comment, or a
                        change of status, priority or due date. Assigning a
                        ticket doesn't count. After rep outreach counts the
                        campaign's orders from customers a rep had actioned a
                        ticket for, placed on or after that first action. Orders
                        placed before the rep got to it count as "without".
                        "Ordered" compares, among customers who had a ticket,
                        the share who ordered after a rep reached out with the
                        share who ordered when no one did. The reached customers
                        are only counted from the day of the outreach, which can
                        only lower their figure. Reps may follow up their most
                        promising contacts first, so this is timing, not proof
                        the follow-up made the difference.
                      </p>
                    </>
                  ))}
              </div>
            </TabsContent>
            <TabsContent value="performance">
              <CampaignPerformanceSection canManage={canManageMarketing} />
            </TabsContent>
          </Tabs>
        </>
      )}

      <CommercialOutcomeOrdersDrawer
        open={isOrdersDrawerOpen}
        onOpenChange={setIsOrdersDrawerOpen}
        // Only null before the first open; the drawer fetches only while
        // open, so this placeholder is never loaded.
        source={
          ordersDrawer?.source ?? {
            kind: "campaign",
            campaignId: "",
            windowDays,
            send: null,
          }
        }
        name={ordersDrawer?.name ?? ""}
        filter={ordersDrawer?.filter ?? "all"}
        title={ordersDrawer?.title ?? ""}
        productKeywords={ordersDrawer?.productKeywords ?? null}
        categories={ordersDrawer?.categories ?? null}
      />

      <CommercialOutcomeEnquiriesDrawer
        open={enquiriesDrawer !== null}
        onOpenChange={(o) => !o && setEnquiriesDrawer(null)}
        campaignId={enquiriesDrawer?.campaignId ?? ""}
        campaignName={enquiriesDrawer?.campaignName ?? ""}
        windowDays={windowDays}
        productKeywords={enquiriesDrawer?.productKeywords ?? null}
        categories={enquiriesDrawer?.categories ?? null}
        send={enquiriesDrawer?.send ?? null}
      />
    </div>
  );
}
